import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FormSection, ProFormConfig } from '../types/formBuilder';
import { saveProFormConfiguration, loadProFormConfiguration } from '../utils/supabaseStorage';
import { parseAdminStaffSession } from '../utils/adminSession';
import { FormBuilder } from '../components/admin/FormBuilder';
import { createNewSection } from '../utils/formBuilderHelpers';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { AdminPageToolbar } from '../components/admin/AdminLayout';

export const CreateProFormPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editFormId = searchParams.get('edit');
  const isEditMode = !!editFormId;

  const [isLoadingForm, setIsLoadingForm] = useState(false);
  const [loadErrorForm, setLoadErrorForm] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [accessRecord, setAccessRecord] = useState<any>(null);

  const [sections, setSections] = useState<FormSection[]>([
    createNewSection(0)
  ]);

  const [proFormConfig, setProFormConfig] = useState<ProFormConfig>({
    formName: '',
    formDescription: '',
    routePath: '',
    requestType: '',
    appId: '',
    creditCost: 10,
    exampleMediaUrl: '',
    exampleMediaType: undefined
  });

  // Load existing pro form data when in edit mode
  useEffect(() => {
    setAccessRecord(parseAdminStaffSession());
  }, []);

  // Separate useEffect for loading existing form data
  useEffect(() => {
    const loadExistingProForm = async () => {
      if (!isEditMode || !editFormId) return;

      setIsLoadingForm(true);
      setLoadErrorForm(null);

      try {
        const result = await loadProFormConfiguration(editFormId);

        if (result.success && result.data) {
          if (accessRecord && accessRecord.role !== 'superadmin' && result.data.hy_user_id !== accessRecord.hy_user_id) {
            setLoadErrorForm('Access denied. You can only edit your own pro forms.');
            return;
          }

          setProFormConfig({
            formName: result.data.form_name || '',
            formDescription: result.data.form_description || '',
            routePath: result.data.route_path || '',
            requestType: result.data.request_type || '',
            appId: result.data.app_id || '',
            creditCost: 10,
            exampleMediaUrl: '',
            exampleMediaType: undefined
          });

          if (result.data.input_schema && Array.isArray(result.data.input_schema)) {
            const convertedSections = [{
              id: 'main-section',
              title: 'Form Fields',
              fields: result.data.input_schema.map((field: any, index: number) => ({
                id: `field-${index}`,
                type: field.type || '',
                label: field.label || '',
                variable_name: field.key || field.variable_name || '',
                additional_variables: field.additional_variables || [],
                additional_variable_types: field.additional_variable_types || undefined,
                required: field.required || false,
                placeholder: field.placeholder || '',
                description: field.description || '',
                advanced: field.advanced || false,
                defaultValue: field.defaultValue,
                hidden: field.hidden || false,
                min: field.min,
                max: field.max,
                step: field.step,
                rows: field.rows,
                options: field.options || [],
                multiple: field.multiple,
                accept: field.accept,
                imageOptions: field.imageOptions || [],
                allowCustomValue: field.allowCustomValue || false,
                customValueLabel: field.customValueLabel || '',
                customButtonLabel: field.customButtonLabel || '',
                customInputPlaceholder: field.customInputPlaceholder || '',
                imageValidationPrompt: field.imageValidationPrompt || undefined,
                enablePromptEnhancement: field.enablePromptEnhancement || false,
                systemInstructions: field.systemInstructions || undefined,
                promptBuilderConfig: field.promptBuilderConfig || undefined
              }))
            }];
            setSections(convertedSections);
          } else {
            setSections([createNewSection(0)]);
          }
        } else {
          setLoadErrorForm(result.error || 'Failed to load pro form configuration');
        }
      } catch (error) {
        setLoadErrorForm(error instanceof Error ? error.message : 'Unknown error occurred');
      } finally {
        setIsLoadingForm(false);
      }
    };

    loadExistingProForm();
  }, [isEditMode, editFormId]); // Remove accessRecord from dependencies

  const goBack = () => {
    navigate('/admin/pro-forms');
  };

  const handleSaveConfiguration = async () => {
    if (!proFormConfig.formName.trim()) {
      setSaveError('Form name is required');
      return;
    }
    if (!proFormConfig.routePath.trim()) {
      setSaveError('Route path is required');
      return;
    }
    if (!proFormConfig.requestType.trim()) {
      setSaveError('Request type is required');
      return;
    }
    if (!proFormConfig.appId.trim()) {
      setSaveError('App ID is required');
      return;
    }
    if (sections.length === 0 || sections.every(s => s.fields.length === 0)) {
      setSaveError('At least one form field is required');
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const result = await saveProFormConfiguration({
        formConfig: proFormConfig,
        sections,
        isUpdate: isEditMode,
        existingFormId: editFormId || undefined,
        currentUserId: accessRecord?.hy_user_id
      });

      if (result.success) {
        const action = isEditMode ? 'updated' : 'created';
        alert(`✅ Pro Form "${proFormConfig.formName}" ${action} successfully!`);
        if (!isEditMode) {
          setProFormConfig({
            formName: '',
            formDescription: '',
            routePath: '',
            requestType: '',
            appId: '',
            creditCost: 10,
            exampleMediaUrl: '',
            exampleMediaType: undefined
          });
          setSections([createNewSection(0)]);
        }
      } else {
        setSaveError(result.error || 'Failed to save configuration');
      }
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-base">
      <AdminPageToolbar
        title={isEditMode ? 'Edit Pro Form' : 'Create Pro Form'}
        subtitle={
          isEditMode
            ? `Editing: ${proFormConfig.formName || editFormId}`
            : 'Add new dynamic pro request form'
        }
      />

      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-4 py-4 pb-8">
          {isLoadingForm ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-4">
                <LoadingSpinner size="lg" />
                <p className="text-th-secondary">Loading pro form configuration...</p>
              </div>
            </div>
          ) : loadErrorForm ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-4 max-w-md">
                <div className="text-status-error text-6xl">⚠️</div>
                <h3 className="text-xl font-bold text-th-primary">Failed to Load Pro Form</h3>
                <p className="text-th-secondary">{loadErrorForm}</p>
                <button onClick={goBack} className="button-primary">
                  Back to Pro Forms
                </button>
              </div>
            </div>
          ) : (
            <FormBuilder
              sections={sections}
              onSectionsChange={setSections}
              onOpenPreview={() => alert('Preview not available for Pro Forms yet.')} // Placeholder
              onOpenFieldSettings={() => alert('Field settings not available for Pro Forms yet.')} // Placeholder
              onOpenJsonAI={() => alert('JSON AI not available for Pro Forms yet.')} // Placeholder
              onOpenPageSettings={() => alert('Page settings not available for Pro Forms yet.')} // Placeholder
              mode="pro-form" // Indicate pro form mode
              proFormConfig={proFormConfig}
              onProFormConfigChange={setProFormConfig}
              onSaveConfiguration={handleSaveConfiguration}
              isSaving={isSaving}
              saveError={saveError}
              isEditMode={isEditMode}
            />
          )}
        </div>
      </main>

      {/* Background Effects */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>
    </div>
  );
};